import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client.js";
import { num } from "@/lib/format.js";

interface StructuralResult {
  systemWeightKg: number;
  windLoadKNm2: number;
  snowLoadKNm2: number;
  snowGroundKNm2: number;
  altitudeM: number;
  snowZone: number;
  windZone: number;
  pressDownKNm2: number;
  upliftKNm2: number;
  selfWeightKNm2: number;
  utilization?: {
    checks: Array<{ id: string; label: string; loadCase: string; edKNm: number; rdKNm: number; unit: string; utilizationPct: number }>;
    governing: { id: string; utilizationPct: number; loadCase: string } | null;
    recommendedMaxSupportSpacingM: number;
    verdict: "ok" | "grenzwertig" | "nicht_erfuellt";
    disclaimer: string;
  };
  riskLevel: "low" | "medium" | "high";
  requiresStaticReport: boolean;
}

interface FormState {
  systemSizeKwp: number;
  roofType: "pitched" | "flat" | "carport" | "facade" | "pv_fence";
  tiltDeg: number;
  altitudeM: number;
  snowZone: number;
  windZone: number;
  buildingAgeYears: number;
  constructionType: "wood" | "steel" | "concrete" | "masonry";
  supportSpacingM: number;
}

const UTIL_VERDICT: Record<string, { text: string; color: string; bg: string }> = {
  ok: { text: "OK (≤ 90 %)", color: "#166534", bg: "var(--color-success-tint, #dcfce7)" },
  grenzwertig: { text: "Grenzwertig (90–100 %)", color: "#92400e", bg: "var(--color-warning-tint, #fef3c7)" },
  nicht_erfuellt: { text: "Nicht erfüllt (> 100 %) — Stützabstand reduzieren", color: "#991b1b", bg: "#fee2e2" },
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "7px 9px",
  border: "1px solid var(--color-border, #cbd5e1)",
  borderRadius: 6,
  fontSize: 13,
};

const RISK_LABEL: Record<string, { text: string; color: string; bg: string }> = {
  low: { text: "Gering", color: "#166534", bg: "var(--color-success-tint, #dcfce7)" },
  medium: { text: "Mittel", color: "#92400e", bg: "var(--color-warning-tint, #fef3c7)" },
  high: { text: "Hoch — Statiknachweis erforderlich", color: "#991b1b", bg: "#fee2e2" },
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4, fontWeight: 500 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function LoadCard({ label, value, unit, tone }: { label: string; value: string; unit: string; tone?: "warn" | undefined }) {
  return (
    <div className="card" style={{ textAlign: "center", padding: "14px 10px" }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: tone === "warn" ? "var(--color-error)" : "var(--color-primary)" }}>
        {value} <span style={{ fontSize: 13, fontWeight: 500 }}>{unit}</span>
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>{label}</div>
    </div>
  );
}

/**
 * Statische VORBEMESSUNG (Schnee-/Windlast nach ÖNORM EN 1991, AT-Zonen,
 * höhenabhängig). KEIN Standsicherheitsnachweis — die kN/m²-Werte dienen der
 * frühen Machbarkeitsabschätzung; der prüfpflichtige Nachweis erfolgt durch eine
 * Fachkraft. Engine: roof-core (computeSnowLoad/Wind/Bemessungslasten).
 */
export function StructuralTab({ projectId, revisionId }: { projectId: string; revisionId: string }) {
  const [form, setForm] = useState<FormState>({
    systemSizeKwp: 10,
    roofType: "pitched",
    tiltDeg: 30,
    altitudeM: 500,
    snowZone: 2,
    windZone: 2,
    buildingAgeYears: 20,
    constructionType: "wood",
    supportSpacingM: 1.2,
  });
  const [result, setResult] = useState<StructuralResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // kWp + Neigung aus der letzten Modulbelegung / Dachfläche vorbefüllen.
  useEffect(() => {
    void apiClient
      .get<Array<{ layoutData?: { systemSizeKwp?: number } }>>(`/projects/${projectId}/planning`)
      .then((r) => {
        if (r.success && Array.isArray(r.data) && r.data.length > 0) {
          const sum = r.data.reduce(
            (a, p) => a + (typeof p.layoutData?.systemSizeKwp === "number" ? p.layoutData.systemSizeKwp : 0),
            0,
          );
          if (sum > 0) setForm((f) => (f.systemSizeKwp === 10 ? { ...f, systemSizeKwp: parseFloat(sum.toFixed(2)) } : f));
        }
      });
    void apiClient
      .get<Array<{ result?: StructuralResult }>>(`/projects/${projectId}/structural`)
      .then((r) => {
        if (r.success && Array.isArray(r.data) && r.data[0]?.result) setResult(r.data[0].result);
      });
    // Seehöhe + Dachneigung aus den Geodaten übernehmen (einheitengleich m→m, °→°).
    // Zonen (Schnee/Wind) bleiben bewusst editierbare Defaults mit HORA-Prüfhinweis —
    // es gibt kein lat/lng→Zone-Mapping im Code (wäre ziviltechnik-nah, net-new).
    void apiClient
      .get<{ buildings: Array<{ elevation?: number; roofSurfaces?: Array<{ tiltDeg?: number; isUsable?: boolean; areaM2?: number }> }> }>(`/projects/${projectId}/geospatial`)
      .then((r) => {
        if (!r.success || !r.data?.buildings?.length) return;
        const b = r.data.buildings[0]!;
        const surfaces = (b.roofSurfaces ?? []).filter((s) => s.isUsable !== false);
        const lead = surfaces.slice().sort((a, c) => (c.areaM2 ?? 0) - (a.areaM2 ?? 0))[0];
        setForm((f) => ({
          ...f,
          altitudeM: f.altitudeM === 500 && typeof b.elevation === "number" ? Math.round(b.elevation) : f.altitudeM,
          tiltDeg:
            f.tiltDeg === 30 && lead && typeof lead.tiltDeg === "number" && Number.isFinite(lead.tiltDeg)
              ? Math.round(lead.tiltDeg)
              : f.tiltDeg,
        }));
      });
  }, [projectId]);

  const handleAssess = async () => {
    setRunning(true);
    setError(null);
    const res = await apiClient.post<{ engineResult?: { data?: StructuralResult } }>(
      `/projects/${projectId}/structural`,
      { revisionId, ...form },
    );
    const d = res.success ? res.data?.engineResult?.data : undefined;
    if (d) setResult(d);
    else setError(res.success ? "Unerwartete Antwort." : res.error.message);
    setRunning(false);
  };

  const risk = result ? RISK_LABEL[result.riskLevel] : null;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Statik — Vorbemessung</h2>
      </div>

      <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#92400e" }}>
        ⚠ Vorbemessung nach ÖNORM EN 1991 (Schnee/Wind, AT-Zonen, höhenabhängig) — <strong>kein Standsicherheitsnachweis</strong>.
        Die Werte dienen der frühen Machbarkeit; der prüfpflichtige Nachweis erfolgt durch eine Fachkraft (Statiker).
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Standort & Anlage</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Anlagengröße (kWp)">
            <input type="number" min="0.5" step="0.5" value={form.systemSizeKwp} style={inputStyle}
              onChange={(e) => setForm((f) => ({ ...f, systemSizeKwp: parseFloat(e.target.value) }))} />
          </Field>
          <Field label="Seehöhe (m)">
            <input type="number" min="0" step="50" value={form.altitudeM} style={inputStyle}
              onChange={(e) => setForm((f) => ({ ...f, altitudeM: parseInt(e.target.value, 10) }))} />
          </Field>
          <Field label="Neigung (°)">
            <input type="number" min="0" max="90" value={form.tiltDeg} style={inputStyle}
              onChange={(e) => setForm((f) => ({ ...f, tiltDeg: parseInt(e.target.value, 10) }))} />
          </Field>
          <Field label="Dachtyp">
            <select value={form.roofType} style={inputStyle}
              onChange={(e) => setForm((f) => ({ ...f, roofType: e.target.value as FormState["roofType"] }))}>
              <option value="pitched">Schräg</option>
              <option value="flat">Flach</option>
              <option value="carport">Carport</option>
              <option value="facade">Fassade</option>
            </select>
          </Field>
          <Field label="Schneezone (1–4)">
            <input type="number" min="1" max="4" value={form.snowZone} style={inputStyle}
              onChange={(e) => setForm((f) => ({ ...f, snowZone: parseInt(e.target.value, 10) }))} />
          </Field>
          <Field label="Windzone (1–4)">
            <input type="number" min="1" max="4" value={form.windZone} style={inputStyle}
              onChange={(e) => setForm((f) => ({ ...f, windZone: parseInt(e.target.value, 10) }))} />
          </Field>
          <Field label="Gebäudealter (Jahre)">
            <input type="number" min="0" step="1" value={form.buildingAgeYears} style={inputStyle}
              onChange={(e) => setForm((f) => ({ ...f, buildingAgeYears: parseInt(e.target.value, 10) }))} />
          </Field>
          <Field label="Konstruktion">
            <select value={form.constructionType} style={inputStyle}
              onChange={(e) => setForm((f) => ({ ...f, constructionType: e.target.value as FormState["constructionType"] }))}>
              <option value="wood">Holz</option>
              <option value="steel">Stahl</option>
              <option value="concrete">Beton</option>
              <option value="masonry">Mauerwerk</option>
            </select>
          </Field>
          <Field label="Stützabstand Schiene (m)">
            <input type="number" min="0.3" max="3" step="0.1" value={form.supportSpacingM} style={inputStyle}
              onChange={(e) => setForm((f) => ({ ...f, supportSpacingM: parseFloat(e.target.value) }))} />
          </Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <button className="btn-primary" onClick={() => void handleAssess()} disabled={running || !revisionId}>
            {running ? "Berechne…" : "Vorbemessung berechnen"}
          </button>
          {!revisionId && <span style={{ marginLeft: 12, fontSize: 12, color: "var(--color-error)" }}>Keine Revision vorhanden</span>}
        </div>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {result && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <LoadCard label="Andruck (Schnee + Eigenlast)" value={num(result.pressDownKNm2, 2)} unit="kN/m²" />
            <LoadCard label="Abheben (Eigenlast − Windsog)" value={num(result.upliftKNm2, 2)} unit="kN/m²" tone={result.upliftKNm2 < 0 ? "warn" : undefined} />
            <LoadCard label="Eigenlast PV" value={num(result.selfWeightKNm2, 2)} unit="kN/m²" />
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
              <div>Bodenschneelast s<sub>k</sub> (Seehöhe {result.altitudeM} m, Zone {result.snowZone})</div>
              <div style={{ textAlign: "right", fontWeight: 600 }}>{num(result.snowGroundKNm2, 2)} kN/m²</div>
              <div>Schneelast am Dach</div>
              <div style={{ textAlign: "right", fontWeight: 600 }}>{num(result.snowLoadKNm2, 2)} kN/m²</div>
              <div>Windlast (Zone {result.windZone})</div>
              <div style={{ textAlign: "right", fontWeight: 600 }}>{num(result.windLoadKNm2, 2)} kN/m²</div>
              <div>Systemgewicht</div>
              <div style={{ textAlign: "right", fontWeight: 600 }}>{num(result.systemWeightKg)} kg</div>
            </div>
          </div>

          {result.utilization && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Bauteil-Auslastung — Montagesystem (ULS, EN 1990)</h3>
              <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10 }}>
                Stützabstand {num(form.supportSpacingM, 2)} m · empfohlen max. {num(result.utilization.recommendedMaxSupportSpacingM, 2)} m
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                    {["Bauteil", "Lastfall", "E_d", "R_d", "Auslastung"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "6px 8px", fontSize: 12, color: "var(--color-text-muted)", fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.utilization.checks.map((c) => (
                    <tr key={c.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "6px 8px", fontWeight: 500 }}>{c.label}</td>
                      <td style={{ padding: "6px 8px" }}>{c.loadCase === "andrueckend" ? "Andruck" : "Abheben"}</td>
                      <td style={{ padding: "6px 8px" }}>{num(c.edKNm, 3)} {c.unit}</td>
                      <td style={{ padding: "6px 8px" }}>{num(c.rdKNm, 3)} {c.unit}</td>
                      <td style={{ padding: "6px 8px", fontWeight: 700, color: c.utilizationPct > 100 ? "var(--color-error)" : c.utilizationPct > 90 ? "#92400e" : "var(--color-success)" }}>
                        {num(c.utilizationPct, 1)} %
                      </td>
                    </tr>
                  ))}
                  {result.utilization.checks.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: "8px", color: "var(--color-text-muted)" }}>Keine Bauteil-Specs — Auslastung nicht berechenbar.</td></tr>
                  )}
                </tbody>
              </table>
              {UTIL_VERDICT[result.utilization.verdict] && (
                <div style={{ marginTop: 10, background: UTIL_VERDICT[result.utilization.verdict]!.bg, borderRadius: 6, padding: "8px 12px", fontSize: 12, color: UTIL_VERDICT[result.utilization.verdict]!.color, fontWeight: 600 }}>
                  Montagesystem: {UTIL_VERDICT[result.utilization.verdict]!.text}
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 11, color: "var(--color-text-muted)" }}>{result.utilization.disclaimer}</div>
            </div>
          )}

          {risk && (
            <div style={{ background: risk.bg, borderRadius: 6, padding: "12px 14px", fontSize: 13, color: risk.color, fontWeight: 600 }}>
              Risiko-Einstufung: {risk.text}
            </div>
          )}
        </>
      )}
    </div>
  );
}
